import React, { useCallback, useEffect, useRef, useState } from 'react';

import CustomLoading from '../../../util_components/custom_loading';
import LoadFailed from '../shared/load_failed';
import { fetch_payout_holidays, post_payout_holiday, post_payout_holiday_remove } from '../api';
import {
  HOLIDAY_NAME_MAX,
  has_error,
  holiday_errors,
  holiday_years,
  holidays_view,
  payout_error
} from './adapters';
import { holiday_add_body, holiday_remove_body } from './requests';

/*
 * the bank holidays that "2 bank working days" (when a teacher may report a payment as not
 * received) skips.
 */
const LOAD_FAILED_TEXT = 'Could not load the bank holidays.';
const SAVE_FAILED_TEXT = 'The holiday may not have been saved. Reload to check.';

const HolidaysPanel = ({ today, on_notify, on_error }) => {
  const years = holiday_years(today);
  const [year, setYear] = useState(years[0]);
  const [state, setState] = useState({ status: 'loading', rows: [], error: '' });
  const [form, setForm] = useState({ date: '', name: '' });
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [removing, setRemoving] = useState('');
  const [error, setError] = useState('');
  const seq = useRef(0);
  const errors = holiday_errors(form.date, form.name);

  const load = useCallback(() => {
    seq.current += 1;
    const mine = seq.current;
    setState((s) => ({ ...s, status: 'loading' }));
    fetch_payout_holidays(year)
      .then((d) => {
        if (mine === seq.current) setState({ status: 'ready', rows: holidays_view(d), error: '' });
      })
      .catch((e) => {
        if (mine !== seq.current) return;
        const v = payout_error(e, LOAD_FAILED_TEXT);
        if (v.kind === 'auth') on_error(e, LOAD_FAILED_TEXT);
        setState({ status: 'error', rows: [], error: v.text });
      });
  }, [year, on_error]);

  useEffect(() => {
    load();
  }, [load]);

  // Both writes answer the saved date's year: show it when it is the year on screen.
  const saved = (d, date, text) => {
    setBusy(false);
    setRemoving('');
    setError('');
    on_notify('success', text);
    if (date.slice(0, 4) === String(year))
      setState({ status: 'ready', rows: holidays_view(d), error: '' });
  };

  const failed = (e) => {
    setBusy(false);
    const v = payout_error(e, SAVE_FAILED_TEXT);
    if (v.kind === 'auth') on_error(e, SAVE_FAILED_TEXT);
    setError(v.text);
  };

  const add = (e) => {
    e.preventDefault();
    setTouched(true);
    if (busy || has_error(errors)) return;
    const body = holiday_add_body(form.date, form.name);
    setBusy(true);
    post_payout_holiday(body)
      .then((d) => {
        setForm({ date: '', name: '' });
        setTouched(false);
        saved(d, body.date, `Bank holiday saved: ${body.date} ${body.name}.`);
      })
      .catch(failed);
  };

  const remove = (date) => {
    if (busy) return;
    setBusy(true);
    post_payout_holiday_remove(holiday_remove_body(date))
      .then((d) => saved(d, date, `Bank holiday ${date} removed.`))
      .catch(failed);
  };

  return (
    <div className="tp-po-holidays">
      <div className="tp-hint tp-po-intro">
        Bank holidays are skipped when counting the 2 bank working days before a teacher can report
        a payment as not received. Weekends are skipped already.
      </div>
      <div className="tp-seg" role="group" aria-label="Year">
        {years.map((y) => (
          <button
            key={y}
            type="button"
            className={year === y ? 'on' : ''}
            aria-pressed={year === y}
            onClick={() => setYear(y)}
          >
            {y}
          </button>
        ))}
      </div>
      <form className="tp-frow" onSubmit={add}>
        <label htmlFor="tp-po-hol-date" className="tp-hint">
          Date
        </label>
        <input
          id="tp-po-hol-date"
          className="tp-input"
          type="date"
          value={form.date}
          onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
        />
        <label htmlFor="tp-po-hol-name" className="tp-hint">
          Name
        </label>
        <input
          id="tp-po-hol-name"
          className="tp-input"
          value={form.name}
          maxLength={HOLIDAY_NAME_MAX}
          onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          placeholder="Diwali"
        />
        <button type="submit" className="tp-btn-out" aria-disabled={busy ? 'true' : undefined}>
          Add holiday
        </button>
      </form>
      {touched && has_error(errors) ? (
        <div className="tp-neg" role="alert">
          {errors.date || errors.name}
        </div>
      ) : null}
      {error ? (
        <div className="tp-neg" role="alert">
          {error}
        </div>
      ) : null}
      {state.status === 'loading' ? <CustomLoading /> : null}
      {state.status === 'error' ? <LoadFailed text={state.error} on_retry={load} /> : null}
      {state.status === 'ready' ? (
        <div className="tp-tablewrap">
          <table className="tp-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Name</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {state.rows.length === 0 ? (
                <tr>
                  <td colSpan={3}>No bank holidays entered for {year}.</td>
                </tr>
              ) : null}
              {state.rows.map((h) => (
                <tr key={h.date}>
                  <td>{h.date}</td>
                  <td>{h.name}</td>
                  <td>
                    {removing === h.date ? (
                      <span>
                        Remove {h.name}?{' '}
                        <button type="button" className="tp-link" onClick={() => remove(h.date)}>
                          {busy ? 'Removing…' : 'Yes, remove'}
                        </button>{' '}
                        <button type="button" className="tp-link" onClick={() => setRemoving('')}>
                          Keep
                        </button>
                      </span>
                    ) : (
                      <button
                        type="button"
                        className="tp-link"
                        aria-label={`Remove the ${h.date} holiday`}
                        onClick={() => setRemoving(h.date)}
                      >
                        Remove
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
};

export default HolidaysPanel;
